import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { isAxiosError } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

/**
 * The API answers 409 with `existingContactId` when the SIRET is already used by another
 * contact of the asset: offer to open that card instead of failing silently.
 * Returns true when the error was handled.
 */
const useSiretConflictHandler = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();

  return useCallback(
    (error: unknown): boolean => {
      if (!isAxiosError(error) || error.response?.status !== 409) return false;
      const existingContactId = (error.response.data as { existingContactId?: string })
        ?.existingContactId;
      if (!existingContactId) return false;

      modals.openConfirmModal({
        title: t('contacts.company.duplicateTitle', 'Cette entreprise est déjà dans vos contacts'),
        children: (
          <Text size="sm">
            {t(
              'contacts.company.duplicateBody',
              'Une fiche avec ce SIRET existe déjà pour ce bien. Vous pouvez l’ouvrir pour la compléter.'
            )}
          </Text>
        ),
        labels: {
          confirm: t('contacts.company.openExisting', 'Ouvrir sa fiche'),
          cancel: t('common.cancel', 'Annuler'),
        },
        onConfirm: () =>
          navigate(`/asset/${assetId}/infos-et-contacts/contacts/${existingContactId}`),
      });
      return true;
    },
    [assetId, navigate, t]
  );
};

export default useSiretConflictHandler;
